import { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Upload,
    Popconfirm,
    message,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import * as LucideIcons from 'lucide-react';
import ServiceApi from '../api/services';
import LangApi from '../api/langs';

const Services = () => {
    const [services, setServices] = useState([]);
    const [langs, setLangs] = useState([]);
    const [selectedLang, setSelectedLang] = useState('ru');
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [modalVisible, setModalVisible] = useState(false);
    const [editing, setEditing] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [videoList, setVideoList] = useState([]);
    const BASE_URL = process.env.REACT_APP_URL;

    const recommendedIcons = [
        'Activity', 'BarChart4', 'Briefcase', 'Coins', 'DollarSign',
        'Layers', 'LineChart', 'Rocket', 'Settings', 'Star',
    ];

    const fetchLangs = async () => {
        const res = await LangApi.getAll();
        setLangs(res.data);
    };

    const fetchServices = async () => {
        setLoading(true);
        try {
            const res = await ServiceApi.getByLocale(selectedLang);
            setServices(res.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLangs();
    }, []);

    useEffect(() => {
        fetchServices();
    }, [selectedLang]);

    const openModal = (record = null) => {
        setEditing(record);
        setModalVisible(true);
        form.resetFields();
        if (record) form.setFieldsValue(record);
    };

    const closeModal = () => {
        setModalVisible(false);
        form.resetFields();
        setEditing(null);
        setFileList([]);
        setVideoList([]);
    };

    const handleSubmit = async (values) => {
        try {
            if (editing) {
                await ServiceApi.update(editing.id, values);
                message.success('Обновлено');
            } else {
                const imageFile = fileList?.[0]?.originFileObj;
                const videoFile = videoList?.[0]?.originFileObj;

                const formData = new FormData();
                formData.append('title', values.title);
                formData.append('short_desc', values.short_desc);
                formData.append('description', values.description);
                formData.append('icon', values.icon);
                formData.append('lang_id', values.lang_id);
                if (imageFile) formData.append('files', imageFile);
                if (videoFile) formData.append('files', videoFile);

                await ServiceApi.create(formData);
                message.success('Создано');
            }

            closeModal();
            fetchServices();
        } catch (err) {
            console.error(err);
            message.error('Ошибка при сохранении');
        }
    };

    const handleDelete = async (id) => {
        await ServiceApi.remove(id);
        fetchServices();
        message.success('Удалено');
    };

    const columns = [
        {
            title: 'Иконка',
            dataIndex: 'icon',
            render: (icon) => {
                const Icon = LucideIcons[icon] || LucideIcons.Star;
                return <Icon className="w-5 h-5 text-gray-700" />;
            },
        },
        {
            title: 'Изображение',
            dataIndex: 'cover_image',
            render: (img) =>
                img ? <img src={`${BASE_URL}${img}`} className="w-20 h-14 object-cover" alt="cover" /> : '—',
        },
        {
            title: 'Видео',
            dataIndex: 'video',
            render: (video) =>
                video ? (
                    <video
                        src={`${BASE_URL}${video}`}
                        controls
                        className="w-32 h-20 object-cover"
                    />
                ) : (
                    '—'
                ),
        },

        { title: 'Заголовок', dataIndex: 'title' },
        { title: 'Описание', dataIndex: 'short_desc' },
        {
            title: 'Действия',
            render: (_, record) => (
                <div className="flex gap-2">
                    <Button onClick={() => openModal(record)} size="small">Редактировать</Button>
                    <Popconfirm title="Удалить?" onConfirm={() => handleDelete(record.id)}>
                        <Button danger size="small">Удалить</Button>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Сервисы</h2>
                <div className="flex gap-2">
                    <Select
                        value={selectedLang}
                        onChange={setSelectedLang}
                        options={langs.map((l) => ({ label: l.name, value: l.code }))}
                        style={{ width: 150 }}
                    />
                    <Button type="primary" onClick={() => openModal()}>Добавить</Button>
                </div>
            </div>

            <Table
                dataSource={services}
                columns={columns}
                rowKey="id"
                loading={loading}
            />

            <Modal
                title={editing ? 'Редактировать' : 'Добавить'}
                open={modalVisible}
                onCancel={closeModal}
                onOk={() => form.submit()}
                okText="Сохранить"
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    {!editing && (
                        <Form.Item name="lang_id" label="Язык" rules={[{ required: true }]}>
                            <Select options={langs.map((l) => ({ label: l.name, value: l.id }))} />
                        </Form.Item>
                    )}
                    <Form.Item name="title" label="Заголовок" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="short_desc" label="Кратко" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="description" label="Полное описание" rules={[{ required: true }]}>
                        <Input.TextArea rows={4} />
                    </Form.Item>

                    <Form.Item name="icon" label="Выбранная иконка">
                        <Input placeholder="Выберите иконку ниже или введите имя" />
                    </Form.Item>

                    <div className="flex flex-wrap gap-2 mb-4">
                        {recommendedIcons.map((iconName) => {
                            const IconComponent = LucideIcons[iconName];
                            return (
                                <Button
                                    key={iconName}
                                    onClick={() => form.setFieldsValue({ icon: iconName })}
                                    className="flex items-center justify-center w-12 h-12 border"
                                >
                                    <IconComponent className="w-5 h-5" />
                                </Button>
                            );
                        })}
                    </div>

                    {!editing && (
                        <>
                            <Form.Item label="Изображение">
                                <Upload
                                    beforeUpload={() => false}
                                    listType="picture"
                                    fileList={fileList}
                                    maxCount={1}
                                    onChange={({ fileList }) => setFileList(fileList)}
                                    accept="image/*"
                                >
                                    <Button icon={<UploadOutlined />}>Загрузить изображение</Button>
                                </Upload>
                            </Form.Item>
                            <Form.Item label="Видео (необязательно)">
                                <Upload
                                    beforeUpload={() => false}
                                    fileList={videoList}
                                    maxCount={1}
                                    onChange={({ fileList }) => setVideoList(fileList)}
                                    accept="video/*"
                                >
                                    <Button icon={<UploadOutlined />}>Загрузить видео</Button>
                                </Upload>
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default Services;
